import {
  AbstractClient,
  Client,
  ClientErrorResponse,
  ClientResponse,
  ClientStatus,
  ClientUrls,
  Conversation,
  ConversationChannel,
  ConversationContext,
  ConversationMessage,
  ConversationMessageStatus,
  ConversationParticipant,
  ConversationStatus,
  ConversationSyncStorageMode,
  GetConversationMessagesParams,
  GetConversationMessagesResponse,
  GetConversationParticipantsParams,
  GetConversationParticipantsResponse,
  LeaveConversationParams,
  ObservabilityConfig,
  SendConversationMessageParams,
  SendConversationTypingParams,
  StartConversationParams,
  StartConversationResponse,
  InProgressParams,
  InProgressResponse,
  isNetworkError,
  ConversationActionParams,
} from '@stripe-internal/conversations-client';
import {GetMessagesResponse} from '@stripe-internal/stripethirdparty/lib/shared/TwilioTypes';
import {pick} from 'lodash';
import {STPTwilioOutputMessage, STPTwilioService, STPUnmounted} from './stp';
import {twilioErrorMessages} from './twilio-error-messages';
import {METRIC} from './metrics';

const CLIENT_TYPE = 'twilio';
const METRIC_TAGS = {client_type: CLIENT_TYPE};
const ERROR_TAGS = METRIC_TAGS;

export class TwilioClient extends AbstractClient implements Client {
  private service: STPTwilioService;

  // This is necessary due to current limitations in our STP Twilio implementation
  // only working on a single channel at a time. To guard against errors from our
  // STP Twilio implementation, the client stores the active `conversationId` and
  // will error if we try to make calls for a different conversation.
  private conversationId: string | null = null;

  constructor({
    clientFetch,
    urls,
    observability,
    service = STPUnmounted,
  }: {
    clientFetch?: typeof fetch;
    urls?: Partial<ClientUrls>;
    observability?: ObservabilityConfig;
    service?: STPTwilioService;
  } = {}) {
    super({clientFetch, urls, observability});
    this.service = service;
  }

  get channel(): ConversationChannel {
    return 'chat';
  }

  get type(): string {
    return CLIENT_TYPE;
  }

  get syncStorageMode(): ConversationSyncStorageMode {
    return 'hydrate';
  }

  init(twilioService: STPTwilioService): void {
    this.service = twilioService;
    if (this.getStatus() === ClientStatus.Uninitialized) {
      this.status$.next(ClientStatus.Initialized);
    }
  }

  async action({
    conversationId,
    action,
  }: ConversationActionParams): Promise<ClientResponse> {
    const actionName = action.name as string;
    const params = {conversationId, actionName};
    this.errors.reportError('[twilio-client] action not supported', {
      tags: params,
    });
    this.incrementAndTrack(METRIC.action_not_supported, {actionName});
    return this.error(400, 'action not supported');
  }

  async disconnect(): Promise<void> {
    this.conversationId = null;
    this.status$.next(ClientStatus.Uninitialized);
    await this.service.shutdown();
  }

  async startConversation(
    params: StartConversationParams,
  ): Promise<StartConversationResponse> {
    try {
      let {conversationId: id, participant: user, token} = params;
      const {
        context,
        syncStorage = this.syncStorageMode,
        connect = true,
        fetch = false,
        uhConversationId,
      } = params;

      if (!token || !user) {
        const {token: fetchedToken, identity} = await this.fetchToken();
        token = fetchedToken;
        if (!user) {
          user = this.userForIdentity(identity);
        }
      }

      if (!id) {
        const {conversationId} = await this.createTwilioChannel(
          context,
          uhConversationId,
        );
        id = conversationId;
      }

      const initializeStartMs = Date.now();
      const initResp = await this.service.init(token);
      this.metrics.timing(
        METRIC.service_init_duration,
        Date.now() - initializeStartMs,
        METRIC_TAGS,
      );

      if ('error' in initResp) {
        this.status$.next(ClientStatus.Error);
        this.incrementAndTrack(METRIC.service_init_error);
        return this.error(400, initResp.error as string);
      }

      const setActiveConversationResponse = await this.setActiveConversation(
        id,
      );
      if ('error' in setActiveConversationResponse) {
        return this.stpError(setActiveConversationResponse.error as string);
      }

      const conversation = new Conversation(this, {
        id,
        token,
        user,
        syncStorage,
        context: {...context},
      });

      if (connect) {
        await conversation.connect({fetch});
      }

      this.incrementAndTrack(METRIC.start_conversation_success);

      return {code: 200, data: {conversation}};
    } catch (e) {
      if (isNetworkError(e)) {
        // return a timeout error instead of throwing for network errors
        return this.error(408, (e as Error).message);
      }

      const error = `exception starting conversation: ${(e as Error).message}`;
      this.emitNewEvent({
        name: 'client-exception',
        error,
      });

      this.incrementAndTrack(METRIC.start_conversation_error);

      // 5xx error will reportError
      return this.error(500, error);
    }
  }

  async leaveConversation({
    participant,
    conversationId,
  }: LeaveConversationParams): Promise<ClientResponse> {
    try {
      const response = await this.service.leaveChannel(participant);
      if ('error' in response) {
        return this.stpError(response.error as string);
      }

      this.emitNewConversationEvent({
        name: 'conversation-participant-left',
        conversationId,
        participant,
      });

      return Promise.resolve({code: 200});
    } catch (e) {
      if (isNetworkError(e)) {
        // return a timeout error instead of throwing for network errors
        return this.error(408, (e as Error).message);
      }

      const error = `exception leaving conversation: ${(e as Error).message}`;
      this.emitNewEvent({
        name: 'client-exception',
        error,
      });
      return this.error(500, error);
    }
  }

  async getConversationMessages({
    token,
    conversationId,
    messageIds,
  }: GetConversationMessagesParams): Promise<GetConversationMessagesResponse> {
    return this.fetchMessages({token, conversationId, messageIds});
  }

  async getConversationParticipants({
    token,
    conversationId,
    participantIds,
  }: GetConversationParticipantsParams): Promise<GetConversationParticipantsResponse> {
    return this.fetchParticipants({token, conversationId, participantIds});
  }

  async sendConversationMessage({
    conversationId,
    message,
  }: SendConversationMessageParams): Promise<ClientResponse> {
    if (this.conversationId !== conversationId) {
      this.errors.reportWarning(
        '[twilio-client] send conversation message conversation id mismatch',
        {
          tags: {
            conversationId,
            activeConversationId: this.conversationId ?? '',
          },
        },
      );
      this.incrementAndTrack(METRIC.conversation_id_mismatch, {
        method: 'sendConversationMessage',
      });
      return this.error(
        400,
        "Conversation id doesn't match active conversation",
      );
    }

    try {
      let response;

      switch (message.type) {
        case 'text-conversation-message': {
          response = await this.service.sendMessage(
            message.text,
            message.nonce,
          );
          break;
        }
        case 'attachment-conversation-message': {
          if (!message.attachment.file) {
            return this.error(400, 'File not provided when sending attachment');
          }

          /*
           * We read the file into a new file to work around an issue with file
           * uploads and Chrome's enforcement of the same-origin policy. See
           * MSE-204 for details.
           */
          response = await this.service.sendFile(
            new File([message.attachment.file], message.attachment.file.name, {
              type: message.attachment.file.type,
            }),
            message.nonce,
          );

          break;
        }
        default: {
          return this.error(400, 'Unexpected message type');
        }
      }

      if (response && 'error' in response) {
        return this.stpError(response.error as string);
      }

      return Promise.resolve({code: 200});
    } catch (e) {
      if (isNetworkError(e)) {
        // return a timeout error instead of throwing for network errors
        return this.error(408, (e as Error).message);
      }

      const error = `exception sending conversation message: ${
        (e as Error).message
      }`;
      this.emitNewEvent({
        name: 'client-exception',
        error,
      });
      return this.error(500, error);
    }
  }

  async sendConversationTyping({
    conversationId,
  }: SendConversationTypingParams): Promise<ClientResponse> {
    if (this.conversationId !== conversationId) {
      return this.error(
        400,
        "Conversation id doesn't match active conversation",
      );
    }

    try {
      const response = await this.service.sendTyping();
      if ('error' in response) {
        return this.stpError(response.error as string);
      }

      return Promise.resolve({code: 200});
    } catch (e) {
      return this.error(400, (e as Error).message);
    }
  }

  async inProgress({
    conversationId,
  }: InProgressParams): Promise<InProgressResponse> {
    if (!conversationId) {
      return {code: 200, data: {inProgress: false}};
    }

    if (this.conversationId === conversationId) {
      return {
        code: 200,
        data: {
          conversationId,
          channel: this.channel,
          clientType: this.type,
          inProgress: true,
        },
      };
    }

    const {token} = await this.fetchToken();

    const initializeStartMs = Date.now();
    const initResp = await this.service.init(token);
    this.metrics.timing(
      METRIC.service_init_duration,
      Date.now() - initializeStartMs,
      METRIC_TAGS,
    );

    if ('error' in initResp) {
      this.status$.next(ClientStatus.Error);
      this.incrementAndTrack(METRIC.service_init_error);
      return this.error(400, initResp.error as string);
    }

    const setActiveConversationResponse = await this.setActiveConversation(
      conversationId,
    );
    if ('error' in setActiveConversationResponse) {
      return {code: 200, data: {inProgress: false}};
    }

    return {
      code: 200,
      data: {
        conversationId,
        channel: this.channel,
        clientType: this.type,
        inProgress: true,
      },
    };
  }

  handleStpMessage = (message: STPTwilioOutputMessage): void => {
    if (message.type !== 'event') {
      return;
    }

    if (message.data?.sid && message.data.sid !== this.conversationId) {
      return;
    }

    switch (message.event) {
      case 'connectionStateChanged': {
        this.handleTwilioConnectionStateChanged(message.data.connectionState);
        break;
      }
      case 'memberJoined': {
        this.handleTwilioParticipantJoined(message.data.identity);
        break;
      }
      case 'memberLeft': {
        this.handleTwilioParticipantLeft(message.data.identity);
        break;
      }
      case 'messageAdded': {
        this.handleTwilioMessageAdded(message.data);
        break;
      }
      case 'typingStarted': {
        this.handleTwilioTypingStarted(message.data.identity);
        break;
      }
      case 'typingEnded': {
        this.handleTwilioTypingEnded(message.data.identity);
        break;
      }
      case 'channelUpdated': {
        this.handleTwilioChannelUpdated(message.data);
        break;
      }
      default:
    }
  };

  private async setActiveConversation(id: string): Promise<ClientResponse> {
    const setActiveChannelResponse = await this.service.setActiveChannel(id);
    if (!setActiveChannelResponse) {
      this.incrementAndTrack(METRIC.active_conversation_not_found);
      return {code: 404, error: `Channel ${id} not found`};
    }

    if ('error' in setActiveChannelResponse) {
      return this.stpError(setActiveChannelResponse.error as string);
    }

    this.conversationId = id;

    return {code: 200};
  }

  private fetchParticipants = async ({
    conversationId,
    participantIds,
  }: GetConversationParticipantsParams): Promise<GetConversationParticipantsResponse> => {
    if (this.conversationId !== conversationId) {
      this.errors.reportWarning(
        '[twilio-client] fetch participants conversation id mismatch',
        {
          tags: {
            conversationId,
            activeConversationId: this.conversationId ?? '',
          },
        },
      );
      this.incrementAndTrack(METRIC.conversation_id_mismatch, {
        method: 'fetchParticipants',
      });
      return this.error(
        400,
        "Conversation id doesn't match active conversation",
      );
    }

    if (!participantIds) {
      // NOTE: Ideally, it wouldn't behave this way, but our current stripethirdparty
      // Twilio implementation doesn't support fetching all the channel's participants.
      // We should always be passing specific participant ids, but even if we do hit this
      // code path, we should be fine as the client will fetch missing participants on
      // demand if missing them.
      return {code: 200, data: {participants: {}}};
    }

    try {
      const participants: Record<string, ConversationParticipant> = {};
      const errors: string[] = [];
      await Promise.all(
        participantIds.map(async (identity) => {
          const memberResponse = await this.service.getMember(identity);
          if (!memberResponse) {
            return {code: 404, error: `Member ${identity} not found`};
          }

          if ('error' in memberResponse) {
            errors.push(memberResponse.error as string);
            return;
          }

          const userResponse = await this.service.getUser(identity);
          if (!userResponse) {
            return {code: 404, error: `User ${identity} not found`};
          }

          if ('error' in userResponse) {
            errors.push(userResponse.error as string);
            return;
          }

          const role =
            memberResponse.data?.attributes?.member_type === 'agent'
              ? 'agent'
              : 'user';
          let displayName = role === 'agent' ? 'Stripe support' : 'User';
          if (role === 'agent') {
            displayName =
              userResponse.data?.friendlyName?.split(' ')[0] ||
              'Stripe support';
          } else {
            displayName = userResponse.data?.friendlyName || 'User';
          }

          const participant: ConversationParticipant = {
            id: identity,
            role,
            displayName,
          };
          this.cacheParticipant(conversationId, participant);

          participants[participant.id] = participant;
        }),
      );

      if (errors.length > 0) {
        return this.error(400, errors.join(', '));
      }

      return {code: 200, data: {participants}};
    } catch (e) {
      if (isNetworkError(e)) {
        // return a timeout error instead of throwing for network errors
        return this.error(408, (e as Error).message);
      }

      const error = `exception fetching participants: ${(e as Error).message}`;
      this.emitNewEvent({
        name: 'client-exception',
        error,
      });
      return this.error(500, error);
    }
  };

  private fetchMessages = async ({
    token,
    conversationId,
    messageIds,
  }: GetConversationMessagesParams): Promise<GetConversationMessagesResponse> => {
    if (this.conversationId !== conversationId) {
      this.errors.reportWarning(
        '[twilio-client] fetch messages conversation id mismatch',
        {
          tags: {
            conversationId,
            activeConversationId: this.conversationId ?? '',
          },
        },
      );
      this.incrementAndTrack(METRIC.conversation_id_mismatch, {
        method: 'fetchMessages',
      });
      return this.error(
        400,
        "Conversation id doesn't match active conversation",
      );
    }

    try {
      const stpMessages: any[] = [];
      let hasMoreMessages = true;
      let indexOffset: number | undefined;
      while (hasMoreMessages) {
        // eslint-disable-next-line no-await-in-loop
        const response = await this.service.getMessages(
          indexOffset,
          'backwards',
        );
        if ('error' in response) {
          return this.stpError(response.error as string);
        }

        const stpMsgData = response.data as GetMessagesResponse;

        // eslint-disable-next-line no-loop-func
        stpMsgData.items.forEach((item: any) => {
          if (!item.attributes?.notification) {
            stpMessages.push(item);
          }
          indexOffset = item.index - 1;
        });

        hasMoreMessages = stpMsgData.hasMore;
      }

      const cachedParticipants: Record<string, ConversationParticipant> = {};
      const participantIdsToFetch: string[] = [];
      stpMessages.forEach((item) => {
        const id = item.author;
        const cached = this.getCachedParticipant(conversationId, id);
        if (cached) {
          cachedParticipants[cached.id] = cached;
        } else {
          participantIdsToFetch.push(id);
        }
      });

      const participantsResponse = await this.fetchParticipants({
        token,
        conversationId,
        participantIds: participantIdsToFetch,
      });
      if ('error' in participantsResponse) {
        return participantsResponse as ClientErrorResponse;
      }

      const messages: Record<string, ConversationMessage> = {};
      const errors: string[] = [];
      stpMessages.forEach((item) => {
        const participant =
          cachedParticipants[item.author] ||
          participantsResponse.data.participants[item.author];

        let message: ConversationMessage;
        if (participant) {
          // we didn't initially pass the message sid through STP,
          // so fallback to the string representation of the index
          const id = item.sid ? item.sid : item.index.toString();

          const timestamp = new Date(item.dateCreated).getTime();

          if (item.type === 'media') {
            message = {
              id,
              timestamp,
              attachment: {
                name: item.media.filename,
                size: item.media.size,
                type: item.media.contentType,
                url: item.media.url,
              },
              participant,
              status: ConversationMessageStatus.Sent,
              nonce: item.attributes?.token,
              type: 'attachment-conversation-message',
            };
          } else {
            message = {
              id,
              timestamp,
              text: item.body,
              participant,
              status: ConversationMessageStatus.Sent,
              nonce: item.attributes?.token,
              type: 'text-conversation-message',
              originalText: item.attributes?.translationData?.originalText,
            };
          }

          messages[message.id] = message;
        } else {
          errors.push(
            `Message participant ${item.author} not found for message: ${this.conversationId}-${item.index}`,
          );
        }
      });

      if (errors.length > 0) {
        return this.error(400, errors.join(', '));
      }

      // todo update STP to allow for fetching specific message ids
      if (messageIds && messageIds.length > 0) {
        return {code: 200, data: {messages: pick(messages, messageIds)}};
      }

      return {code: 200, data: {messages}};
    } catch (e) {
      if (isNetworkError(e)) {
        // return a timeout error instead of throwing for network errors
        return this.error(408, (e as Error).message);
      }

      const error = `exception fetching messages: ${(e as Error).message}`;
      this.emitNewEvent({
        name: 'client-exception',
        error,
      });
      return this.error(500, error);
    }
  };

  private handleTwilioConnectionStateChanged = async (
    connectionState: string,
  ): Promise<void> => {
    switch (connectionState) {
      case 'connecting': {
        const prev = this.getStatus();
        if (
          prev === ClientStatus.Connecting ||
          prev === ClientStatus.Reconnecting
        ) {
          break;
        }

        let next: ClientStatus;
        if (
          prev === ClientStatus.Uninitialized ||
          prev === ClientStatus.Initialized
        ) {
          next = ClientStatus.Connecting;
        } else {
          next = ClientStatus.Reconnecting;
        }
        this.status$.next(next);
        break;
      }
      case 'connected': {
        this.status$.next(ClientStatus.Connected);
        break;
      }
      case 'disconnecting':
      case 'disconnected': {
        await this.disconnect();
        break;
      }
      case 'denied': {
        this.status$.next(ClientStatus.Reconnecting);
        this.incrementAndTrack(METRIC.connection_state_denied);
        // refresh token and reconnect
        break;
      }
      default:
    }
  };

  private handleTwilioParticipantJoined = async (
    identity: string,
  ): Promise<void> => {
    if (!this.conversationId) {
      return;
    }

    const {conversationId} = this;

    const cached = this.getCachedParticipant(conversationId, identity);
    if (cached) {
      this.emitNewConversationEvent({
        name: 'conversation-participant-joined',
        conversationId,
        participant: cached,
      });
    }

    try {
      const resp = await this.fetchParticipants({
        conversationId,
        participantIds: [identity],
      });
      if ('error' in resp) {
        this.emitNewConversationEvent({
          name: 'conversation-error',
          conversationId,
          error: `[handleParticipantJoined] ${resp.error}`,
        });
        return;
      }

      const {participants} = resp.data;
      const participant = participants[identity];

      this.cacheParticipant(conversationId, participant);

      if (!cached) {
        this.emitNewConversationEvent({
          name: 'conversation-participant-joined',
          conversationId,
          participant,
        });
      }
    } catch (e) {
      if (isNetworkError(e)) {
        return;
      }

      const error = `exception handling participant joined event: ${
        (e as Error).message
      }`;
      this.emitNewEvent({
        name: 'client-exception',
        error,
      });
      this.errors.reportError(error, {tags: ERROR_TAGS});
    }
  };

  private handleTwilioParticipantLeft = async (
    identity: string,
  ): Promise<void> => {
    if (!this.conversationId) {
      return;
    }

    const {conversationId} = this;

    const cached = this.getCachedParticipant(conversationId, identity);
    if (cached) {
      this.emitNewConversationEvent({
        name: 'conversation-participant-left',
        conversationId,
        participant: cached,
      });
      return;
    }

    try {
      const resp = await this.fetchParticipants({
        conversationId,
        participantIds: [identity],
      });
      if ('error' in resp) {
        this.emitNewConversationEvent({
          name: 'conversation-error',
          conversationId,
          error: `[handleParticipantLeft] ${resp.error}`,
        });
        return;
      }

      const {participants} = resp.data;
      const participant = participants[identity];

      this.cacheParticipant(conversationId, participant);

      if (!cached) {
        this.emitNewConversationEvent({
          name: 'conversation-participant-left',
          conversationId,
          participant,
        });
      }
    } catch (e) {
      if (isNetworkError(e)) {
        // return a timeout error instead of throwing for network errors
        return;
      }

      const error = `exception handling participant left event: ${
        (e as Error).message
      }`;
      const eventId = this.errors.reportError(error, {tags: ERROR_TAGS});
      this.emitNewEvent({
        name: 'client-exception',
        error,
        eventId,
      });
    }
  };

  private handleTwilioMessageAdded = async (data: any): Promise<void> => {
    if (data.attributes?.notification || !this.conversationId) {
      // these events are apparently ignored as they are member joining/leaving events
      return;
    }

    if (this.conversationId) {
      const messageId = data.sid ? data.sid : data.index.toString();
      this.emitNewConversationEvent({
        name: 'conversation-message-added',
        conversationId: this.conversationId as string,
        participantId: data.author,
        messageId,
        nonce: data.attributes?.token,
      });
    }
  };

  private handleTwilioTypingStarted = async (
    participantId: string,
  ): Promise<void> => {
    if (!this.conversationId) {
      return;
    }

    if (this.conversationId) {
      this.emitNewConversationEvent({
        name: 'conversation-typing-started',
        conversationId: this.conversationId,
        participantId,
      });
    }
  };

  private handleTwilioTypingEnded = async (
    participantId: string,
  ): Promise<void> => {
    if (!this.conversationId) {
      return;
    }

    if (this.conversationId) {
      this.emitNewConversationEvent({
        name: 'conversation-typing-ended',
        conversationId: this.conversationId,
        participantId,
      });
    }
  };

  private handleTwilioChannelUpdated = async (data: any): Promise<void> => {
    if (!this.conversationId) {
      return;
    }

    let status: ConversationStatus = ConversationStatus.Active;
    if (data.status === 'INACTIVE') {
      status = ConversationStatus.Inactive;
    }

    if (this.conversationId) {
      this.emitNewConversationEvent({
        name: 'conversation-updated',
        conversationId: this.conversationId,
        status,
      });
    }
  };

  protected stpError(error: string): Promise<ClientErrorResponse> {
    if (twilioErrorMessages.some((twilioError) => error.match(twilioError))) {
      this.incrementAndTrack(METRIC.stp_error);
      // 5xx error will reportError
      return this.error(500, error);
    }
    return this.error(400, error);
  }

  protected error = async (
    code: number,
    error: string,
  ): Promise<ClientErrorResponse> => {
    return super.error(code, error, ERROR_TAGS);
  };

  private fetchToken = async (): Promise<{token: string; identity: string}> => {
    if (!this.tokenUrl) {
      throw new Error('not implemented');
    }

    const resp = await this.fetch(this.tokenUrl);
    const data = await resp.json();

    const {token, identity} = data;

    return {token, identity};
  };

  private createTwilioChannel = async (
    context: ConversationContext = {},
    uhConversationId: string | undefined = undefined,
  ): Promise<{conversationId: string}> => {
    if (!this.conversationUrl) {
      throw new Error('not implemented');
    }

    const analyticsEventId = await this.analytics.requestAnalyticsEventId();

    const {topic, audience, entryPoint} = context;

    const resp = await this.fetch(this.conversationUrl, {
      method: 'POST',
      body: JSON.stringify({
        topic,
        audience,
        entrypoint: entryPoint,
        channel_type: 'live_chat',
        analytics_event_id: analyticsEventId,
        universal_help_conversation_id: uhConversationId,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const data = await resp.json();

    const {conversationId} = data;

    return {conversationId};
  };

  private userForIdentity = (
    identity: string,
    role: 'user' | 'agent' = 'user',
  ): ConversationParticipant => {
    return {
      role,
      id: identity,
      displayName: identity,
    };
  };

  private incrementAndTrack(
    name: string,
    params: Record<string, string> = {},
  ): void {
    const metricParams = {...METRIC_TAGS, ...params};
    this.metrics.increment(name, metricParams);
    this.analytics.track(name, metricParams);
  }
}
